"use client";

import { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useStore } from "@/hooks/system/useStore";
import S from "./index.module.scss";
import { GetProductModelResource } from "@/api/generated-api";
import { useProductCheckout } from "../../hooks/useProductCheckout";
import AuthModal from "@/views/auth";
import VariantOptionOptionGroup from "./components/variant-option-group/variant-option-option-group";
import VariantOptionProductGroup from "./components/variant-option-group/variant-option-product-group";

interface ProductConfiguratorProps {
  product: GetProductModelResource;
}

const PRODUCT_SCROLL_RESTORATION_KEY = "catalog-detail-product-scroll-position";

type ConfiguratorGroup =
  | { type: "option"; group: GetProductModelResource["options"][number] }
  | { type: "product"; group: GetProductModelResource["products"][number] };

const ProductConfigurator = ({ product }: ProductConfiguratorProps) => {
  const { syncProduct, formattedPrice, getSelectedOptionId, selectOption } = useStore("catalogDetail");
  const { handleBuyProduct, authModalProps } = useProductCheckout();
  const router = useRouter();

  const handleProductSelect = useCallback(
    (selectedProductId: number) => {
      if (selectedProductId !== product.id) {
        sessionStorage.setItem(PRODUCT_SCROLL_RESTORATION_KEY, String(window.scrollY));
        router.push(`/catalog/${selectedProductId}`, { scroll: false });
      }
    },
    [product.id, router],
  );

  const configuratorGroups = useMemo<ConfiguratorGroup[]>(
    () =>
      [
        ...product.options.map(group => ({ type: "option" as const, group })),
        ...product.products.map(group => ({ type: "product" as const, group })),
      ].sort((a, b) => a.group.index - b.group.index),
    [product.options, product.products],
  );

  useEffect(() => {
    syncProduct(product);
  }, [product, syncProduct]);

  useEffect(() => {
    const savedPosition = sessionStorage.getItem(PRODUCT_SCROLL_RESTORATION_KEY);

    if (!savedPosition) return;

    const parsedPosition = Number(savedPosition);

    if (!Number.isFinite(parsedPosition)) {
      sessionStorage.removeItem(PRODUCT_SCROLL_RESTORATION_KEY);
      return;
    }

    requestAnimationFrame(() => {
      window.scrollTo({
        top: parsedPosition,
        behavior: "auto",
      });
      sessionStorage.removeItem(PRODUCT_SCROLL_RESTORATION_KEY);
    });
  }, [product.id]);

  return (
    <div className={S.panel} id="purchase">
      {configuratorGroups.map(item => {
        if (item.type === "option") {
          return (
            <VariantOptionOptionGroup
              key={`${item.type}-${item.group.id}`}
              title={item.group.title}
              items={item.group.items}
              selectedId={getSelectedOptionId(item.group.id)}
              onSelect={id => {
                selectOption(item.group.id, id);
              }}
            />
          );
        }

        return (
          <VariantOptionProductGroup
            key={`${item.type}-${item.group.id}`}
            title={item.group.title}
            items={item.group.items}
            selectedId={product.id}
            onSelect={id => {
              handleProductSelect(id);
            }}
          />
        );
      })}

      <div className={S.divider} />

      <div className={S.footer}>
        <p className={S.price}>{formattedPrice}</p>
        <div className={S.actions}>
          <Link href="/configurator" className={S.outlineButton}>
            Индивидуальный запрос
          </Link>
          <button onClick={handleBuyProduct} className={S.solidButton}>
            {product.is_preorder ? "Предзаказ" : "Купить"}
          </button>
        </div>
      </div>
      <AuthModal {...authModalProps} />
    </div>
  );
};

export default observer(ProductConfigurator);
